// Grid.tsx - Polymorphic Grid layout system
import React, { forwardRef } from "react";
import Box from "../Box/Box";
import { GridBaseProps, GridComponent } from "./Grid.types";

/**
 * Grid component - A polymorphic CSS Grid layout component
 *
 * @example
 * // Basic usage
 * <Grid gridTemplateColumns="repeat(3, 1fr)" gap="medium">
 *   <Box>Item 1</Box>
 *   <Box>Item 2</Box>
 * </Grid>
 *
 * // Using columns shorthand
 * <Grid columns={3} gap="large">
 *   <Box>Item 1</Box>
 *   <Box>Item 2</Box>
 * </Grid>
 *
 * // As a section
 * <Grid as="section" gridTemplateColumns="1fr 2fr" gap="medium">
 *   <Box>Sidebar</Box>
 *   <Box>Main content</Box>
 * </Grid>
 */
export const Grid = forwardRef(function Grid(
  props: GridBaseProps & { as?: React.ElementType },
  ref
) {
  const { inline = false, columns, as, ...rest } = props;

  // Convert columns shorthand to gridTemplateColumns
  const gridTemplateColumns = columns
    ? `repeat(${columns}, 1fr)`
    : props.gridTemplateColumns;

  return (
    <Box
      ref={ref}
      as={as}
      display={inline ? "inline-grid" : "grid"}
      gridTemplateColumns={gridTemplateColumns}
      {...(rest as Record<string, unknown>)}
    />
  );
}) as GridComponent;

// Set display name for debugging
Grid.displayName = "Grid";

export default Grid;
